import React from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormCheckboxField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { InterestRateConfigDto } from 'api/Interest-Rates/InterestRateDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { WalletTypeDto } from 'api/Wallet/Types/WalletTypeDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface EditInterestRateFormValues {
  id: string
  tickmillCompany: NameDto
  country: NameDto<string>
  currency: NameDto<CurrencyType>
  walletType: NameDto
  enabled: boolean
}

const EditInterestRateFormUI: React.FC<FormikProps<EditInterestRateFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Form>
      <DataGrid
        rightAddon={
          <ButtonGroup>
            <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
              Submit
            </Button>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Interest Rate' columns={2}>
            <FormSelectField
              name='tickmillCompany.id'
              label='Tickmill Company'
              options={props.tickmillCompanies}
            />
            <FormSelectField name='country.id' label='Country' options={props.countries} />
            <FormSelectField
              name='currency.id'
              label='Currency'
              options={(props.currencies || []).map((currency) => ({
                id: currency.id,
                name: currency.id,
              }))}
            />
            <FormSelectField
              name='walletType.id'
              label='Wallet Type'
              options={props.walletTypes || []}
            />
            <FormCheckboxField name='enabled' label='Status' />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

interface OuterProps {
  data: InterestRateConfigDto
  permissions: PermissionDto[]
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]
  currencies: WalletCurrencyDto[]
  walletTypes: WalletTypeDto[]

  onSubmit(values: FormSubmitValues<EditInterestRateFormValues>): Promise<void>
}

export const EditInterestRateForm = withFormik<OuterProps, EditInterestRateFormValues>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data.id,
      tickmillCompany: data.tickmillCompany,
      currency: data.currency,
      country: data.country,
      walletType: data.walletType,
      enabled: data.enabled,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props))
    } finally {
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<EditInterestRateFormValues> = {}
    return errors
  },
  enableReinitialize: true,
})(EditInterestRateFormUI)

const dataToSubmit = (
  values: EditInterestRateFormValues,
  props: OuterProps
): FormSubmitValues<EditInterestRateFormValues> => {
  const { tickmillCompanies, countries, currencies, walletTypes } = props

  const tickmillCompany = tickmillCompanies.find((x) => x.id === values.tickmillCompany.id)
  const country = countries.find((x) => x.id === values.country.id)
  const currency = currencies.find((x) => x.id === values.currency.id)
  const walletType = walletTypes.find((x) => x.id === values.walletType.id)

  return {
    ...values,
    tickmillCompany: tickmillCompany || values.tickmillCompany,
    country: country || values.country,
    currency: currency || values.currency,
    walletType: walletType || values.walletType,
  }
}
